import { useCallback, useEffect, useState } from 'react';
import {
  fetchESignatureCredentialStatus,
  type ESignatureCredentialStatus,
} from './esignatureCredentialApi';

let cached: ESignatureCredentialStatus | null = null;
let inFlight: Promise<ESignatureCredentialStatus> | null = null;
// Bumped on every invalidation. A request in flight when the cache is dropped
// cannot be cancelled, so it carries the generation it was issued under and its
// result is discarded if that no longer matches — otherwise a response fetched
// before the credential changed could land afterwards and restore the old answer.
let generation = 0;

/** Drop the cache after any change, so the next dialog reads fresh state. */
export function invalidateESignatureCredentialStatus(): void {
  cached = null;
  inFlight = null;
  generation += 1;
}

export interface UseESignatureCredentialResult {
  status: ESignatureCredentialStatus | null;
  isLoading: boolean;
  error: Error | null;
  /** Refetch, showing the loading state. */
  refresh: () => Promise<void>;
  /** Refetch in the background, keeping the current status on screen. */
  revalidate: () => Promise<void>;
  /** True when the last background recheck failed, so `status` may be stale. */
  isStale: boolean;
}

/**
 * Loads whether the signed-in user has an e-signature password.
 *
 * The signing dialog needs this to choose between asking for the existing
 * password and walking a first-time signer through creating one. Cached per page
 * session like `useActiveAttestations`, because every sign dialog on the page
 * asks the same question and the answer only changes when this user changes it.
 */
export function useESignatureCredential(): UseESignatureCredentialResult {
  const [status, setStatus] = useState<ESignatureCredentialStatus | null>(
    cached
  );
  const [isLoading, setIsLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);

  const load = useCallback(
    async ({ keepStatusOnError = false } = {}): Promise<void> => {
      const issuedAt = generation;
      try {
        const p = inFlight ?? fetchESignatureCredentialStatus();
        inFlight = p;
        const data = await p;
        if (issuedAt !== generation) {
          // The cache was invalidated while this was in flight, so this answer
          // predates whatever changed. Drop it and let the newer load win.
          return;
        }
        cached = data;
        inFlight = null;
        setStatus(data);
        setError(null);
        setIsStale(false);
      } catch (e) {
        inFlight = null;
        console.error('Failed to load e-signature credential status', e);
        if (keepStatusOnError) {
          // A background recheck failed. Keep the status already on screen —
          // replacing a usable password form with an error screen over a
          // transient blip would stop a clinician signing for no reason — but
          // mark it stale so callers do not submit against a status that may
          // predate a change made elsewhere.
          setIsStale(true);
          return;
        }
        setError(
          new Error(
            'Could not check your e-signature setup. Refresh the page or try again shortly.'
          )
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const refresh = useCallback(async (): Promise<void> => {
    invalidateESignatureCredentialStatus();
    setIsLoading(true);
    await load();
  }, [load]);

  const revalidate = useCallback(async (): Promise<void> => {
    // No loading state, and a failure leaves the current status in place: the
    // caller already has something usable on screen and only wants it corrected
    // if it has since changed. Used when a signing dialog reopens, because
    // creating the credential during a signature happens on the server and would
    // otherwise leave this cache claiming the user still has none.
    invalidateESignatureCredentialStatus();
    await load({ keepStatusOnError: true });
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    if (cached) {
      setStatus(cached);
      setIsLoading(false);
      return;
    }

    void (async () => {
      await load();
      if (cancelled) {
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load]);

  return { status, isLoading, error, refresh, revalidate, isStale };
}
